import { ref, type Ref } from 'vue';
import { readSession, writeSession } from '@/lib/sessionPersist';

// Crash-recovery for unsaved editor drafts across an inactivity logout.
//
// When the idle timer fires with dirty editors open, App.vue snapshots each dirty file
// here BEFORE the sign-out navigates away. sessionStorage survives the logout->login
// round-trip in the same tab (it's per-tab, keyed by origin, and the AAD redirect
// returns to the same tab), so on the next sign-in App.vue can offer to restore them.
// It deliberately does NOT survive a tab close — an abandoned tab shouldn't resurrect
// edits days later.
//
// Pure functions, no store import (keeps the lib free of the store->api->lib cycle).
// App.vue builds the snapshot array from the files store and hands it in.

const KEY = 'draft-recovery.v1';

// "Managed unload": an app-initiated full-page navigation where DirtyGuard's native
// beforeunload prompt must NOT fire. Two callers set it:
//   - idle auto-logout: drafts are already snapshotted for recovery, and an unattended
//     logout mustn't wedge on a prompt nobody can dismiss.
//   - manual Sign out: the user has already answered a PrimeVue confirm, so the native
//     prompt would be a redundant second dialog.
// It is deliberately NOT set for genuine tab-close / refresh / external navigation —
// browsers only allow their own UI there, so the native beforeunload stays as the
// last-resort guard for those. Module-scoped: resets to false on the next page load,
// so the guard is fully armed again for the next session.
let managedUnload = false;

export function beginManagedUnload(): void {
  managedUnload = true;
}

export function isManagedUnload(): boolean {
  return managedUnload;
}

export interface RecoverableDraft {
  // File path — the files store's key and the server file identity.
  path: string;
  // The dirty editor contents at logout time. This is what gets restored.
  draft: string;
  // server.contents at capture time. Retained for provenance and possible future
  // "the file changed on disk since you were editing" detection — the recovery UI no
  // longer shows an in-panel diff (the operator sees it in the real editor instead).
  baseline: string;
  // server.sha256 at capture time. Recorded for provenance / future "the file changed
  // on disk since you were edited" detection; recovery itself re-fetches fresh.
  sha256: string;
  // Epoch ms — shown in the recovery list so the operator knows how old the draft is.
  capturedAt: number;
}

// Module-level reactive UI bridge (same pattern as lib/realtime.ts's exported `status`).
// `recoverableCount` lets a persistent affordance in AppShell appear whenever drafts are
// pending, so a user who closed the drawer can get back to the recovery list WITHOUT a
// page reload. `recoveryDrawerVisible` is the single source of truth for the app-root
// drawer's open state — App.vue binds it, AppShell's affordance flips it on.
// Kept in sync on every mutation below so the badge is always accurate regardless of
// which path (recover / discard / clear) changed storage.
export const recoverableCount: Ref<number> = ref(0);
export const recoveryDrawerVisible: Ref<boolean> = ref(false);

function syncCount(): void {
  recoverableCount.value = readRecoverableDrafts().length;
}

export function openRecoveryDrawer(): void {
  recoveryDrawerVisible.value = true;
}

// Recompute once at module load so AppShell's affordance is correct on the first paint
// after a login that carried drafts through sessionStorage.
syncCount();

export function saveRecoverableDrafts(drafts: RecoverableDraft[]): void {
  if (drafts.length === 0) return;
  writeSession(KEY, drafts);
  syncCount();
}

export function readRecoverableDrafts(): RecoverableDraft[] {
  const list = readSession<RecoverableDraft[]>(KEY, []);
  // Defensive: anything malformed in storage degrades to "nothing to recover" rather
  // than throwing into the app-root setup.
  return Array.isArray(list) ? list.filter(isWellFormed) : [];
}

export function removeRecoverableDraft(path: string): void {
  const remaining = readRecoverableDrafts().filter((d) => d.path !== path);
  if (remaining.length === 0) {
    clearRecoverableDrafts();
  } else {
    writeSession(KEY, remaining);
    syncCount();
  }
}

export function clearRecoverableDrafts(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // sessionStorage disabled / quota — best-effort, mirrors sessionPersist.
  }
  syncCount();
}

function isWellFormed(d: unknown): d is RecoverableDraft {
  if (typeof d !== 'object' || d === null) return false;
  const r = d as Record<string, unknown>;
  return (
    typeof r.path === 'string'
    && typeof r.draft === 'string'
    && typeof r.baseline === 'string'
    && typeof r.sha256 === 'string'
    && typeof r.capturedAt === 'number'
  );
}
